import { Component, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { MessageService, ConfirmationService } from 'primeng/api';
import { first } from 'rxjs/operators';
import { TagService, CategoryCount } from '../tag.service';
import { AlertService } from '@app/_services';

@Component({
	selector: 'app-tag-category-manage-dialog',
	templateUrl: './tag-category-manage-dialog.component.html',
	styleUrls: ['./tag-category-manage-dialog.component.css'],
	providers: [MessageService, ConfirmationService],
	standalone: false
})
export class TagCategoryManageDialogComponent implements OnInit
{
	categories: CategoryCount[] = [];
	loading = false;

	// row-level edit state
	editingName: string | null = null;	// category currently in edit mode
	editedValue: string = '';			// typed new name

	// tracks whether any mutation happened — parent can refresh list on close
	anyChange = false;

	constructor(
		private dialogRef: MatDialogRef<TagCategoryManageDialogComponent>,
		private tagService: TagService,
		private alertService: AlertService,
		private messageService: MessageService,
		private confirmationService: ConfirmationService
	) { }

	ngOnInit(): void
	{
		this.loadCategories();
	}

	loadCategories(): void
	{
		this.loading = true;
		this.tagService.getCategories().pipe(first()).subscribe(
			(res: any) =>
			{
				this.categories = (res && res.data && res.data.categories) || [];
			},
			(err: any) =>
			{
				console.log('getCategories FAIL: ' + JSON.stringify(err));
				this.alertService.error(err);
				this.loading = false;
			},
			() => { this.loading = false; }
		);
	}

	startEdit(cat: CategoryCount): void
	{
		this.editingName = cat.name;
		this.editedValue = cat.name;
	}

	cancelEdit(): void
	{
		this.editingName = null;
		this.editedValue = '';
	}

	isEditing(cat: CategoryCount): boolean
	{
		return this.editingName === cat.name;
	}

	saveRename(cat: CategoryCount): void
	{
		const oldName = cat.name;
		const newName = (this.editedValue || '').trim();

		if (!newName)
		{
			this.messageService.add({ severity: 'warn', summary: 'Invalid', detail: 'Name cannot be empty.', life: 3000 });
			return;
		}
		if (newName === oldName)
		{
			this.cancelEdit();
			return;
		}

		// Check for merge scenario — target category already exists
		const existing = this.categories.find(c => c.name === newName);
		if (existing)
		{
			const mergedCount = (existing.count || 0) + (cat.count || 0);
			this.confirmationService.confirm({
				message: `Category '${ newName }' already exists with ${ existing.count } tag(s). Merge '${ oldName }' (${ cat.count } tag(s)) into '${ newName }'? ${ mergedCount } tag(s) will share this category.`,
				header: 'Merge Category',
				icon: 'pi pi-exclamation-triangle',
				accept: () => { this.doRename(oldName, newName); },
				reject: () => { /* keep edit state so the user can amend */ }
			});
		}
		else
		{
			this.doRename(oldName, newName);
		}
	}

	private doRename(oldName: string, newName: string): void
	{
		this.loading = true;
		this.tagService.renameCategory(oldName, newName).pipe(first()).subscribe(
			(res: any) =>
			{
				const affected = (res && res.data && res.data.affected) || 0;
				this.messageService.add({
					severity: 'success',
					summary: 'Success',
					detail: `Renamed '${ oldName }' → '${ newName }' (${ affected } tag(s) updated).`,
					life: 3000
				});
				this.anyChange = true;
				this.cancelEdit();
				this.loadCategories();
			},
			(err: any) =>
			{
				console.log('renameCategory FAIL: ' + JSON.stringify(err));
				this.alertService.error(err);
				this.loading = false;
			}
		);
	}

	onClear(cat: CategoryCount): void
	{
		this.confirmationService.confirm({
			message: `Clear category '${ cat.name }' from ${ cat.count } tag(s)? The tags will remain but will appear as uncategorized. This does NOT delete any tags.`,
			header: 'Confirm Clear',
			icon: 'pi pi-exclamation-triangle',
			accept: () => { this.doClear(cat.name); },
			reject: () => { /* no-op */ }
		});
	}

	private doClear(name: string): void
	{
		this.loading = true;
		this.tagService.clearCategory(name).pipe(first()).subscribe(
			(res: any) =>
			{
				const affected = (res && res.data && res.data.affected) || 0;
				this.messageService.add({
					severity: 'success',
					summary: 'Success',
					detail: `Cleared category from ${ affected } tag(s).`,
					life: 3000
				});
				this.anyChange = true;
				this.loadCategories();
			},
			(err: any) =>
			{
				console.log('clearCategory FAIL: ' + JSON.stringify(err));
				this.alertService.error(err);
				this.loading = false;
			}
		);
	}

	/* ------------------------------------------------------------------
	 * R4 — Per-category color picker
	 * ------------------------------------------------------------------ */

	/** Debounce timers keyed by category name so rapid color-picker drags don't spam the API. */
	private colorDebounce: Record<string, any> = {};

	/** Bound to <input type="color"> on each row. Pushes the new color via debounced PATCH. */
	onColorChange(cat: CategoryCount, newColor: string): void
	{
		// Reflect locally for instant feedback
		cat.color = newColor;
		// Debounce — color picker fires many input events while dragging
		if (this.colorDebounce[cat.name]) {
			clearTimeout(this.colorDebounce[cat.name]);
		}
		this.colorDebounce[cat.name] = setTimeout(() => {
			delete this.colorDebounce[cat.name];
			this.persistColor(cat.name, newColor);
		}, 400);
	}

	/** "Reset" button — clears the color for a category. */
	clearColor(cat: CategoryCount): void
	{
		cat.color = null;
		this.persistColor(cat.name, null);
	}

	private persistColor(name: string, color: string | null): void
	{
		this.tagService.updateCategoryColor({ name, color }).pipe(first()).subscribe(
			(res: any) =>
			{
				const affected = (res && res.data && res.data.affected) || 0;
				this.messageService.add({
					severity: 'success',
					summary: 'Color saved',
					detail: color
						? `Set ${ name } color to ${ color } (${ affected } tag(s)).`
						: `Cleared color on ${ affected } tag(s).`,
					life: 3000
				});
				this.anyChange = true;
			},
			(err: any) =>
			{
				console.log('updateCategoryColor FAIL: ' + JSON.stringify(err));
				this.alertService.error(err);
			}
		);
	}

	close(): void
	{
		this.dialogRef.close({ changed: this.anyChange });
	}
}
